import { memo, useCallback } from 'react';
import { Box, FormControl, Grid, TextField, Typography } from '@mui/material';
import { MasterAccountFieldProps } from './MasterAccountField.types';

export const MasterAccountField = memo<MasterAccountFieldProps>(
  ({
    organizationId,
    errors,
    register,
    t,
    className,
    onValidate,
    isValidating = false,
    validationError,
    isValid = false,
  }) => {
    const isDisabled = !!organizationId;
    const hasValidationError = validationError && !isDisabled;
    const showSuccess = isValid && !isDisabled && !isValidating;

    const getHelperText = useCallback(() => {
      if (hasValidationError) {
        return validationError;
      }
      if (showSuccess) {
        return t('regValidOrganization');
      }
      return t('regMasterAccountNameHelper');
    }, [hasValidationError, validationError, showSuccess, t]);

    const getEndAdornment = useCallback(() => {
      if (isValidating) {
        return (
          <Box display="flex" alignItems="center" pr={1}>
            <Typography variant="caption" color="text.secondary">
              {t('regValidating')}
            </Typography>
          </Box>
        );
      }
      if (showSuccess) {
        return (
          <Box display="flex" alignItems="center" pr={1}>
            <Typography variant="caption" color="success.main">
              ✓
            </Typography>
          </Box>
        );
      }
      return null;
    }, [isValidating, showSuccess, t]);

    return (
      <Grid item xs={12} md={6} className={className}>
        <FormControl fullWidth>
          <TextField
            id="masterAccountName"
            label={t('regMasterAccountName')}
            error={!!errors.masterAccountName || !!hasValidationError}
            helperText={getHelperText()}
            disabled={isDisabled}
            {...register('masterAccountName', {
              required: true,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (onValidate && !isDisabled) {
                  onValidate(e.target.value);
                }
              },
            })}
            InputProps={{
              endAdornment: getEndAdornment(),
            }}
          />
        </FormControl>
      </Grid>
    );
  }
);

MasterAccountField.displayName = 'MasterAccountField';

export default MasterAccountField;
